import { isDefined } from '@sgme/fp';
import classnames from 'classnames';
import { EmailServiceModal } from 'components/EmailService/EmailServiceModal/EmailServiceModal';
import { BlotterPanelHeaderLayout } from 'components/shared/Blotter/BlotterPanel/BlotterPanelHeader/BlotterPanelHeaderLayout';
import { FormattedMessage } from 'components/shared/Intl';
import { useBlotterPanelContext } from 'hooks/useBlotterPanelContext';
import type { IntlKey } from 'locales';
import type { BlotterItemAction } from 'models/blotters/BlotterItemAction';
import type { BlotterType } from 'models/blotters/BlotterType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { FC, ReactNode, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';

export type BlotterPanelTabType = 'SUMMARY' | 'REGULATORY' | 'ORDER_LIMIT' | 'MISSED_DEAL_FEEDBACK';

export interface BlotterPanelHeaderStateProps {
  tabs: BlotterPanelTabType[];
  actions: BlotterItemAction[];
}

export interface BlotterPanelHeaderActionTriggerProps {
  onAddToNewOrdersBasket: (tabName: string) => void;
  onSendEmailClicked: () => void;
  onMissedDealFeedbackTabSelected: () => void;
}

export type BlotterPanelHeaderDispatchProps = BlotterPanelHeaderActionTriggerProps & {
  onClosed: () => void;
};

export interface BlotterPanelHeaderOwnProps {
  blotterItem: IBlotterItem;
  blotterType: BlotterType;
  isLoading: boolean;
}

export type BlotterPanelHeaderProps = BlotterPanelHeaderStateProps &
  BlotterPanelHeaderDispatchProps &
  BlotterPanelHeaderOwnProps;

function getBlotterPanelTabHeaderTranslationKey(tabType: BlotterPanelTabType): IntlKey & `blotterPanel.tab.${string}` {
  switch (tabType) {
    case 'SUMMARY':
      return 'blotterPanel.tab.SUMMARY';
    case 'REGULATORY':
      return 'blotterPanel.tab.REGULATORY';
    case 'ORDER_LIMIT':
      return 'blotterPanel.tab.ORDER_LIMIT';
    case 'MISSED_DEAL_FEEDBACK':
      return 'blotterPanel.tab.MISSED_DEAL_FEEDBACK';
  }
}
export const BlotterPanelHeaderComponent: FC<BlotterPanelHeaderProps> = ({
  blotterType,
  isLoading,
  actions,
  tabs,
  onMissedDealFeedbackTabSelected,
  onClosed,
  ...actionTriggerProps
}) => {
  const { activeTab, setActiveTab, blotterItem } = useBlotterPanelContext()!;
  const { formatMessage } = useIntl();
  const onTabHrefClicked = useCallback(
    (tab: BlotterPanelTabType) => {
      setActiveTab(tab);
      if (tab === 'MISSED_DEAL_FEEDBACK') {
        onMissedDealFeedbackTabSelected();
      }
    },
    [setActiveTab, onMissedDealFeedbackTabSelected],
  );

  useDefaultToFirstTabOnUnknownTab(tabs, activeTab, setActiveTab);
  return (
    <BlotterPanelHeaderLayout onClosed={onClosed}>
      <ul className="nav w-100 d-flex flex-row align-items-center justify-content-start">
        {tabs.map(tab => (
          <li className="nav-item" key={tab} data-e2e="blotter-panel-tab">
            <a
              onClick={() => onTabHrefClicked(tab)}
              className={classnames(
                'nav-link fs-3 p-0 my-0 ms-0 me-1',
                activeTab === tab ? 'active' : 'text-secondary',
              )}
              data-e2e={`blotter-panel-tab-${tab.toLowerCase()}`}
            >
              <FormattedMessage id={getBlotterPanelTabHeaderTranslationKey(tab)} />
            </a>
          </li>
        ))}

        <li className="d-flex justify-content-end align-items-center flex-grow-1">
          {blotterType === 'quotes' && isLoading ? (
            <Skeleton width="8em" />
          ) : (
            actions.map((action, index) => (
              <div key={index} className="d-inline-block" data-e2e="blotter-panel-header-right-items">
                {getActionButton(
                  action,
                  { ...actionTriggerProps, onMissedDealFeedbackTabSelected },
                  formatMessage({ id: `quote.products.${blotterItem?.productSubType}` as IntlKey }),
                )}
              </div>
            ))
          )}
        </li>
      </ul>
    </BlotterPanelHeaderLayout>
  );
};

BlotterPanelHeaderComponent.displayName = 'BlotterPanelHeaderComponent';

function useDefaultToFirstTabOnUnknownTab(
  tabs: BlotterPanelTabType[],
  activeTab: BlotterPanelTabType | undefined,
  setActiveTab: (tab: BlotterPanelTabType) => void,
) {
  useEffect(() => {
    if (tabs.length > 0 && isDefined(activeTab) && !tabs.includes(activeTab)) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab, setActiveTab]);
}

function getActionButton(
  action: BlotterItemAction,
  actionTriggers: BlotterPanelHeaderActionTriggerProps,
  translatedProductSubtype: string,
  isDisabled?: boolean,
): ReactNode {
  switch (action.type) {
    case 'AddToNewOrdersBasket':
      return (
        <button
          className="btn btn-icon-end btn-flat-secondary"
          data-e2e="quote-blotter-panel-add-to-basket"
          onClick={() => actionTriggers.onAddToNewOrdersBasket(translatedProductSubtype)}
          disabled={action.isDisabled || isDisabled}
        >
          <FormattedMessage id={`blotterPanel.actions.${action.translationKey}` as any} />
          <i className="icon ms-1">add</i>
        </button>
      );
    case 'SendEmail':
      return (
        <>
          <button
            className="btn btn-icon  btn-flat-secondary"
            data-e2e="blotter-panel-send-email"
            onClick={actionTriggers.onSendEmailClicked}
            disabled={action.isDisabled || isDisabled}
          >
            <i className="icon">mail_outline</i>
          </button>
          <EmailServiceModal />
        </>
      );
    default:
      return null;
  }
}
